/**
 * Showcase carousel with optional seamless infinite loop.
 * When data-infinite is present: clones slides before/after and repositions at clone boundaries.
 * When absent: normal finite scroll with arrow state management.
 */
class ShowcaseLoop extends HTMLElement {
  /** @type {HTMLElement} */
  #scroller;
  /** @type {HTMLElement[]} */
  #realSlides = [];
  /** @type {number} */
  #realCount = 0;
  /** @type {boolean} */
  #repositioning = false;
  /** @type {HTMLButtonElement|null} */
  #prevBtn = null;
  /** @type {HTMLButtonElement|null} */
  #nextBtn = null;

  get #infinite() {
    return this.hasAttribute('data-infinite');
  }

  get #shouldCenter() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile) return this.classList.contains('showcase-carousel-layout--center-mobile');
    return this.classList.contains('showcase-carousel-layout--center-desktop');
  }

  connectedCallback() {
    this.#scroller = this.querySelector('[data-scroller]');
    if (!this.#scroller) return;

    const selector = this.getAttribute('slide-selector') || ':scope > [data-slide]';
    this.#realSlides = [...this.#scroller.querySelectorAll(selector)];
    this.#realCount = this.#realSlides.length;

    if (this.#realCount < 2) return;

    if (this.#infinite) {
      this.#cloneSlides();
    }

    this.#listen();
    this.#setupArrows();

    requestAnimationFrame(() => {
      // Only use the middle-slide offset when centering is active for this viewport
      const initial = this.#shouldCenter
        ? parseInt(this.getAttribute('initial-slide') || '0') || 0
        : 0;
      if (this.#infinite) {
        this.#scrollTo(this.#realCount + initial, false);
      } else {
        this.#scrollTo(initial, false);
        this.#updateArrowState();
      }
    });
  }

  // ── Clone management ──────────────────────────────────────────────

  #cloneSlides() {
    const frag_before = document.createDocumentFragment();
    const frag_after = document.createDocumentFragment();

    for (let i = 0; i < this.#realCount; i++) {
      frag_before.appendChild(this.#makeClone(this.#realSlides[i]));
      frag_after.appendChild(this.#makeClone(this.#realSlides[i]));
    }

    this.#scroller.prepend(frag_before);
    this.#scroller.append(frag_after);
  }

  /** @param {HTMLElement} slide */
  #makeClone(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-slide');
    clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    return clone;
  }

  // ── Scroll helpers ────────────────────────────────────────────────

  /** All children (clones + real) */
  get #allItems() {
    return [...this.#scroller.children];
  }

  /**
   * Scroll to the item at `index`.
   * Uses center or start alignment based on the current breakpoint setting.
   * @param {number} index - index within #allItems
   * @param {boolean} smooth
   */
  #scrollTo(index, smooth = true) {
    const items = this.#allItems;
    const target = items[index];
    if (!target) return;

    let left;
    if (this.#shouldCenter) {
      left = target.offsetLeft - (this.#scroller.offsetWidth - target.offsetWidth) / 2;
    } else {
      left = target.offsetLeft;
    }

    this.#scroller.scrollTo({
      left: Math.max(0, left),
      behavior: smooth ? 'smooth' : 'instant',
    });
  }

  /**
   * Returns the index (within #allItems) of the current snapped item.
   * When snap is center → finds item closest to viewport center.
   * When snap is start  → finds item closest to scroller left edge.
   */
  #getCurrentIndex() {
    const items = this.#allItems;
    let best = 0;
    let bestDist = Infinity;

    if (this.#shouldCenter) {
      const scrollCenter = this.#scroller.scrollLeft + this.#scroller.offsetWidth / 2;
      for (let i = 0; i < items.length; i++) {
        const el = items[i];
        const dist = Math.abs(el.offsetLeft + el.offsetWidth / 2 - scrollCenter);
        if (dist < bestDist) { bestDist = dist; best = i; }
      }
    } else {
      const scrollLeft = this.#scroller.scrollLeft;
      for (let i = 0; i < items.length; i++) {
        const el = items[i];
        const dist = Math.abs(el.offsetLeft - scrollLeft);
        if (dist < bestDist) { bestDist = dist; best = i; }
      }
    }
    return best;
  }

  // ── Boundary repositioning (infinite mode only) ────────────────────

  #checkBoundary = () => {
    if (!this.#infinite) {
      this.#updateArrowState();
      return;
    }
    if (this.#repositioning) return;

    const idx = this.#getCurrentIndex();
    const n = this.#realCount;

    // Real slides live at indices [n, 2n-1]
    if (idx < n) {
      this.#reposition(idx + n);
    } else if (idx >= n * 2) {
      this.#reposition(idx - n);
    }
  };

  /** @param {number} targetIndex */
  #reposition(targetIndex) {
    this.#repositioning = true;
    this.#scrollTo(targetIndex, false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#repositioning = false;
      });
    });
  }

  // ── Arrow state (finite mode) ─────────────────────────────────────

  #updateArrowState() {
    if (this.#infinite) return;
    const s = this.#scroller;
    const atStart = s.scrollLeft <= 1;
    const atEnd = s.scrollLeft + s.offsetWidth >= s.scrollWidth - 1;
    if (this.#prevBtn) this.#prevBtn.disabled = atStart;
    if (this.#nextBtn) this.#nextBtn.disabled = atEnd;
  }

  // ── Event listeners ───────────────────────────────────────────────

  #listen() {
    this.#scroller.addEventListener('scrollend', this.#checkBoundary);
  }

  #setupArrows() {
    this.#prevBtn = this.querySelector('[data-action="prev"]');
    this.#nextBtn = this.querySelector('[data-action="next"]');

    this.#prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const idx = this.#getCurrentIndex();
      if (!this.#infinite && idx <= 0) return;
      this.#scrollTo(idx - 1, true);
    });

    this.#nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const idx = this.#getCurrentIndex();
      const max = this.#allItems.length - 1;
      if (!this.#infinite && idx >= max) return;
      this.#scrollTo(idx + 1, true);
    });
  }
}

if (!customElements.get('showcase-loop')) {
  customElements.define('showcase-loop', ShowcaseLoop);
}
